/**
 * orderService
 *  - Capa de dominio para creación de pedidos (Single Responsibility)
 *  - No conoce la UI (componentes), solo datos planos
 *  - No conoce directamente Vuex: recibe los datos necesarios como argumentos
 *
 * Esto facilita testear la construcción del payload y respetar SOLID.
 */

import { createOrder } from "@/helpers/apiHelper";
import groupBy from "lodash/groupBy";
import moment from "moment-timezone";

function toBackendUnitDiscount(lineDiscount, quantity) {
  const safeDiscount = Number(lineDiscount || 0);
  const safeQuantity = Number(quantity || 1);
  if (safeQuantity <= 0) return safeDiscount;
  return Number((safeDiscount / safeQuantity).toFixed(6));
}

/**
 * Convierte la programación de la UI (scheduledDay + scheduledTime) en una
 * fecha/hora ISO en la zona horaria de la tienda.
 *
 * @param {string} scheduledDay   - "Today", "Tomorrow" o "YYYY-MM-DD"
 * @param {string} scheduledTime  - p.ej. "2:30 PM"
 * @param {string} timezone       - zona horaria de la tienda (IANA)
 * @returns {string|null} ISO datetime o null si no es válido
 */
function buildServeAtISO(scheduledDay, scheduledTime, timezone) {
  if (!scheduledDay || !scheduledTime) return null;
  const tz = timezone || "UTC";
  let baseDate;
  if (scheduledDay === "Today") baseDate = moment().tz(tz);
  else if (scheduledDay === "Tomorrow")
    baseDate = moment()
      .tz(tz)
      .add(1, "day");
  else baseDate = moment.tz(scheduledDay, "YYYY-MM-DD", tz);
  const parsed = moment(scheduledTime, ["h:mm A", "H:mm"]);
  if (!baseDate.isValid() || !parsed.isValid()) return null;
  baseDate.set({
    hour: parsed.hour(),
    minute: parsed.minute(),
    second: 0,
    millisecond: 0
  });
  return baseDate.toISOString();
}

/**
 * Construye el payload de pedido para `createOrder`
 *
 * @param {Object} params
 * @param {string} params.storeAlias
 * @param {Object[]} params.items                 - items del carrito (cartItems normalizados)
 * @param {number} params.subtotal
 * @param {number} params.discountTotal
 * @param {number} params.totalTax
 * @param {number} params.tipAmount
 * @param {number} params.deliveryFee
 * @param {Object} params.orderConfig             - { type, timing, scheduledDay, scheduledTime }
 * @param {Object} params.store                   - store actual (getStore getter)
 * @param {Object} params.customer                - { firstName, lastName, email, phone, cardName }
 * @param {boolean} params.smsConsent
 * @param {string} [params.diningOptionName]      - Nombre legible de dining option (pickup / delivery / etc.)
 * @param {Object} [params.diningOptionObject]    - Objeto de dining option (para id, fees, etc.)
 * @param {string|null} [params.serveAt]          - Fecha/hora de servicio si aplica
 * @param {string} [params.timezone]              - Zona horaria de la tienda (IANA) para programar serve_at
 * @param {Object|string|null} [params.deliveryAddress] - Dirección de entrega (objeto o string)
 * @param {number} [params.convenienceTotal]      - Convenience fee calculado si aplica
 * @param {string|number|null} [params.paymentTypeId]   - ID del método de pago seleccionado
 * @param {Object|null} [params.paymentDetails]   - Detalles de pago (Stripe token o detalles de tarjeta manual)
 * @returns {Object} payload listo para API
 */
export function buildOrderPayload({
  storeAlias,
  items,
  subtotal,
  discountTotal,
  totalTax,
  tipAmount,
  deliveryFee,
  orderConfig,
  store,
  customer,
  smsConsent,
  diningOptionName,
  diningOptionObject,
  serveAt,
  timezone,
  deliveryAddress,
  convenienceTotal,
  paymentTypeId,
  paymentDetails,
  idempotencyKey
}) {
  const safeSubtotal = Number(subtotal || 0);
  const safeDiscount = Number(discountTotal || 0);
  const safeTax = Number(totalTax || 0);
  const safeTip = Math.round(Number(tipAmount || 0) * 100) / 100;
  const safeDelivery = Number(deliveryFee || 0);
  const safeConvenience = Number(convenienceTotal || 0);

  // Determine if tax is inclusive
  const isTaxInclusive =
    store?.tax_option === "inclusive" || store?.isStoreTaxInclusive === true;

  // Base total (subtotal + tax + delivery). Tips and surcharge are stored separately.
  const baseTotal = isTaxInclusive
    ? safeSubtotal + safeDelivery
    : safeSubtotal + safeTax + safeDelivery;

  const normalizedItems =
    items?.map((item) => {
      // Excluir propiedades solo de UI que no deben ir en el payload
      const {
        selectedModifiers,
        note,
        notes: _ignoredNotes,
        ...restItem
      } = item;

      // Eliminar explícitamente campos de UI del objeto base
      delete restItem.instructions;
      delete restItem.active;
      delete restItem.alertMessage;
      delete restItem.cartId;
      delete restItem.requiresConfirmation;
      delete restItem.itemTotal;
      delete restItem.itemTotalWithoutDiscount;
      delete restItem.wholeDiscount;
      delete restItem.discount_total;

      const modifiers = selectedModifiers || item.modifiers || [];
      const modifiersTotal = modifiers.reduce(
        (sum, mod) => sum + (Number(mod.price) || 0),
        0
      );
      const lineSubtotal =
        (Number(item.price) + modifiersTotal) * Number(item.quantity || 1);
      const lineDiscount =
        (Number(item.discount) || 0) + (Number(item.wholeDiscount) || 0);
      const unitDiscount = toBackendUnitDiscount(
        lineDiscount,
        item.quantity || 1
      );
      const discountedLineSubtotal = Math.max(lineSubtotal - lineDiscount, 0);

      // Alinear con payload de checkout legacy: asegurar campos cost, discount, product, notes
      // El cost debe ser el precio base del producto (item.price), no el cost del objeto
      const cost =
        item.cost != null && item.cost !== 0
          ? Number(item.cost)
          : Number(item.price || 0);

      const discount =
        unitDiscount ||
        (item.discount_total != null
          ? toBackendUnitDiscount(item.discount_total, item.quantity || 1)
          : 0);

      // En el payload legacy, "product" es el ID del producto, no el objeto completo
      const product =
        (item.product && item.product.id != null && item.product.id) ||
        (typeof item.product === "number" ? item.product : null) ||
        (item.id != null ? item.id : null);

      const notes = note || _ignoredNotes || "";
      const promotionLabels = Array.isArray(item.promotionLabels)
        ? [...new Set(item.promotionLabels.filter(Boolean))]
        : [];
      const promotionApplied = promotionLabels.length > 0 && lineDiscount > 0;

      // Crear modifiersGroupped agrupando modifiers por groupName (igual que en root project)
      const modifiersGroupped =
        modifiers.length > 0 ? groupBy(modifiers, "groupName") : {};

      // Calculate grand_total for the item
      const itemTaxAmount = (Number(item.tax) || 0) * (item.quantity || 1);
      const itemGrandTotal = isTaxInclusive
        ? discountedLineSubtotal
        : discountedLineSubtotal + itemTaxAmount;

      return {
        ...restItem,
        cost,
        discount,
        product,
        notes,
        // These fields are ignored by older backend serializers, but are useful
        // for any direct POS/realtime consumers because they identify the
        // discount as promotion-owned rather than manual.
        isPromotionItem: promotionApplied,
        promotion_applied: promotionApplied,
        applied_promotions: promotionLabels.map((promotionName) => ({
          promotionName
        })),
        modifiers, // Incluir array de modifiers
        modifiersGroupped, // Incluir modifiers agrupados por groupName
        void_status: 0,
        subtotal: discountedLineSubtotal,
        grand_total: itemGrandTotal
      };
    }) || [];

  const computedServeAt =
    orderConfig?.timing === "scheduled"
      ? buildServeAtISO(
          orderConfig.scheduledDay,
          orderConfig.scheduledTime,
          timezone
        )
      : null;

  // Aceptar objeto o string para dirección de entrega
  let deliveryAddressString = "";
  if (typeof deliveryAddress === "string") {
    deliveryAddressString = deliveryAddress;
  } else if (deliveryAddress && typeof deliveryAddress === "object") {
    const addr = deliveryAddress;
    deliveryAddressString += addr.address_1 ? `${addr.address_1}, ` : "";
    deliveryAddressString += addr.address_2 ? `${addr.address_2}, ` : "";
    deliveryAddressString += addr.city ? `${addr.city}, ` : "";
    deliveryAddressString += addr.state ? `${addr.state}, ` : "";
    deliveryAddressString += addr.postal_code ? `${addr.postal_code}, ` : "";
    deliveryAddressString += addr.country ? `${addr.country}` : "";
  }

  // Extract address fields from deliveryAddress if it's an object
  const addressFields =
    typeof deliveryAddress === "object" && deliveryAddress
      ? {
          address_1: deliveryAddress.address_1 || "",
          address_2: deliveryAddress.address_2 || "",
          city: deliveryAddress.city || "",
          country: deliveryAddress.country || "",
          postal_code: deliveryAddress.postal_code || "",
          state: deliveryAddress.state || ""
        }
      : {
          address_1: "",
          address_2: "",
          city: "",
          country: "",
          postal_code: "",
          state: ""
        };

  const customerPayload = {
    first_name: customer.firstName,
    last_name: customer.lastName,
    email: customer.email,
    phone: customer.phone,
    address: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      phone: customer.phone,
      ...addressFields
    }
  };

  return {
    store_name: storeAlias,
    items: normalizedItems,
    discount_total: Number(safeDiscount.toFixed(2)),
    tax_total: safeTax,
    // Alinear con root: enviar surcharge separado y grand_total = base_total (sin restar surcharge)
    surcharge_value: Number(safeConvenience.toFixed(2)),
    subtotal: Number(safeSubtotal.toFixed(2)),
    grand_total: Number(baseTotal.toFixed(2)),
    open_date: new Date().toISOString(),
    dining_option: diningOptionName || orderConfig?.type || "pickup",
    // dining_option_type should be the ID of the dining option object (same as root: this.dining_option_object.id)
    // Use the ID from diningOptionObject, same as root: this.dining_option_object.id
    dining_option_type:
      diningOptionObject && diningOptionObject.id
        ? diningOptionObject.id
        : null,
    customer: customerPayload,
    serve_at: serveAt || computedServeAt,
    delivery_address: deliveryAddressString,
    tips_total: safeTip,
    related_store: store?.id || null,
    desirable_payment_type: paymentTypeId || null,
    payment_details: paymentDetails || null, // Stripe token o detalles de tarjeta manual, o null si no aplica
    send_sms_notification: !!smsConsent,
    // Client idempotency key: a retried submit reuses it so the backend returns
    // the already-created order instead of charging/creating a duplicate.
    idempotency_key: idempotencyKey || null
  };
}

/**
 * Envía el pedido a la API usando el payload ya construido
 * Separa construcción de payload (dominio) de IO (HTTP).
 *
 * @param {Object} payload - Objeto devuelto por `buildOrderPayload`
 * @returns {Promise<any>}
 */
export async function submitOrder(payload) {
  return createOrder(payload);
}
